import React, {createRef, Fragment, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import axios from "axios";
import { Grid, InputAdornment, IconButton } from "@material-ui/core";
import { GetApp } from '@material-ui/icons';

import {MaskedText, DatePicker, FormDialog, ComboSelect, AircraftMultiSelect, FormSelect} from "../../../../controls";
import ScheduledTripLegEditor from "./ScheduledTripLegEditor";
import BuildPrimaryRouteFromLegs from '../../utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRoutesFromLegs from '../../utils/BuildOnwardRoutesFromLegs';
import BuildTruckRoutesFromLegs from '../../utils/BuildTruckRoutesFromLegs';
import Validator from "../../validators/AddScheduledTripValidator";
import useTripDepartmentUsers from "../../../../services/auth/useTripDepartmentUsers";

const AddScheduledTripDialog = (SystemData, apiSitePrefix, extraKeys, handleAdd, formatChars, classes, ownerDepartment) => {
  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;
  const tripOwnerDepartment = ownerDepartment;

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    childRecords: null,
    title: 'Add Scheduled Trip'
  });

  const minDate = dialogStates.parentRecord && dialogStates.parentRecord.length > 0?moment.utc(dialogStates.parentRecord[0].startDate):moment.utc().add(1, 'day');
  const maxDate = dialogStates.parentRecord && dialogStates.parentRecord.length > 0?moment.utc(dialogStates.parentRecord[0].endDate):moment.utc().add(3, 'year');
  const initialDate = minDate.toISOString();
  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [tripOwnerDepartment]});

  const open = (record, parentRecord, title) => {
    setDialogStates({...dialogStates, showDialog:true, record, parentRecord, title});
  }
  const close = () => {
    setDialogStates({...dialogStates, showDialog:false, record: {}, parentRecord:null, title: 'Add Scheduled Trip'});
  }

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const handleAddSector = () => {
    if (eventBus) {
      eventBus.trigger('ScheduledTrip.AddSector');
    }
  }

  const handleLegsChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (changes && changes.record) {
      const legs = changes.record.legs;
      record['legs'] = legs;
      recordChange({...record, legs});

      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  const handleGetNextTripNo = (record, changeRecord) => {
    const {startDate, endDate} = record;
    const requestNo = ++requestRef.current;
    axios.get(
      `${apiSitePrefix}/scheduledtripno/next/${startDate}/${endDate}`
    ).then(trip => {
      if (requestNo === requestRef.current) {
        if (trip.data) {
            changeRecord({...record, tripNo: trip.data});
        }
      }
    });
  }

  const Dialog = (props) => {
    if (dialogStates.showDialog) {
      return (
        <FormDialog
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          classes={classes}
          validator={Validator}
          buttons = {{
            addSector: {icon: "add_circle", key: "Add Sector", label: "Add Sector", color: "primary"},
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              const record = state.record;
              handleAdd.mutate({
                extraKeys,
                record,
                postSuccess: () => {
                  setDialogStates({...dialogStates, showDialog: false, record: null})
                },
                postError: (data) => {
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred.`);
                  }
                }
              });
            } else if (state.buttonPressed === "addSector") {
              handleAddSector();
            } else {
              setDialogStates({...dialogStates, showDialog: false, record: null});
            }
          }}

        >
          <Grid container checkChildren={true} spacing={3}>
          <Grid item xs={2} checkChildren={true}>
              <DatePicker
                id="startDate"
                label="Start Date"
                style={{width: "100%"}}
                minDate={initialDate}
                maxDate={maxDate}
                defaultValue={initialDate}
                disableToolbar={true}
                autoOk={true}
                format={dateFormat}
              />
            </Grid>
            <Grid item xs={2} checkChildren={true}>
              {(record) => (
                <DatePicker
                  id="endDate"
                  label="End Date"
                  style={{width: "100%"}}
                  minDate={record.startDate}
                  maxDate={maxDate}
                  defaultValue={record.startDate}
                  disableToolbar={true}
                  autoOk={true}
                  format={dateFormat}
                />
              )}
            </Grid>
            <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div>
                <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Trip Number
                </label>

                <MaskedText
                  id="tripNo"
                  style={{width: "100%", marginTop: 1, display: "inline-block"}}
                  mask="99"
                  maskChar={""}
                  alwaysShowMask={false}
                  formatChars={formatChars}
                  forceUpperCase={true}
                  defaultValue=""
                  inputLabelProps={{shrink: true}}
                  readOnly={record.recordType==="OVERRIDE"}
                  disabled={record.recordType==="OVERRIDE"}
                  inputProps={record.recordType==="OVERRIDE"?undefined:{
                    endAdornment:(
                      <InputAdornment position="end">
                        <IconButton
                          style={{padding:0}}
                          aria-label="Get Next Available Trip No"
                          onClick={()=>{
                            handleGetNextTripNo(record, changeRecord);
                          }}
                        >
                          <GetApp/>
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
            )}
          </Grid>
          <Grid item xs={2} checkChildren={true}>
            {(record) => (
              <ComboSelect
                id="frequencyType"
                label="Frequency"
                defaultValue={"1"}
                group="FREQUENCY TYPES"
                subGroup=""
                readOnly={record.recordType==="OVERRIDE"}
                disabled={record.recordType==="OVERRIDE"}
                //style={{marginTop:2}}
              />
            )}
            </Grid>
            <Grid item xs={1} checkChildren={true}>
            {(record) => (
              <ComboSelect
                id="dow"
                label="Day"
                defaultValue={"1"}
                group="DAY OF WEEK"
                subGroup=""
                readOnly={record.recordType==="OVERRIDE"}
                disabled={record.recordType==="OVERRIDE"}
                //style={{marginTop:2}}
              />
            )}
            </Grid>
            <Fragment checkChildren={true}>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {record =>
                  <FormSelect
                    id="commercialOwnerId"
                    label="Account Manager"
                    options={userOptions}
                    readOnly={false}
                    value={record.commercialOwnerId}
                    onChange={(value, props) => {
                      props.onChange(value);
                    }}
                    style={{width: 200}}
                  />
                }
              </Grid>
              <Grid item xs={1} checkChildren={true}>
                <MaskedText
                  id="uniqueCostingNo"
                  label="Costing Number"
                  style={{width: "100%"}}
                  mask="UUU-9999999"
                  maskChar={""}
                  alwaysShowMask={false}
                  formatChars={formatChars}
                  forceUpperCase={true}
                  defaultValue=""
                  inputLabelProps={{shrink: true}}
                  onChange={(value, props) => {
                    const requestNo = ++requestRef.current;
                    axios.get(
                      `${apiSitePrefix}/mcsdata/costingexists/${value}/___`
                    ).then(costingExists => {
                      if (requestNo === requestRef.current) {
                        if (costingExists.data) {
                          props.recordChange({tripRoute: costingExists.data.routing, uniqueCostingNo: value});
                        }
                      }
                    });
                    props.onChange(value);
                  }}
                />
              </Grid>
            </Fragment>
            <Grid item xs={6} checkChildren={true}>
              <AircraftMultiSelect
                id="initialAircraft"
                label="Initial Aircraft"
                style={{width: "100%"}}
              />
            </Grid>
            <Grid item xs={6} checkChildren={true}>
              <AircraftMultiSelect
                id="aircraftRotation"
                label="Aircraft Rotation"
                style={{width: "100%"}}
              />
            </Grid>

            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Route
                </label>
              </div>
              <div id="primaryRoute" style={{paddingTop: 6}}>
                {(rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="onwardRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Onward Routes
                </label>
              </div>
              <div id="onwardRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildOnwardRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
            <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="truckRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Trucking
                </label>
              </div>
              <div id="truckRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildTruckRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
              <ScheduledTripLegEditor
                tripRecord={dialogStates.record}
                handleUpdate={handleAdd}
                extraKeys={extraKeys}
                canEdit={true}
                classes={classes}
                onChange={handleLegsChanged}
              />
            </Grid>
          </Grid>
        </FormDialog>
      )
    }
    return (<></>);
  }
  return {
    Dialog,
    open,
    close
  }

}

export default AddScheduledTripDialog;
